import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import classNames from 'classnames';
import OpenAPI from '../endpoint/Openapi';
import type { components } from '../endpoint/Schema';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { applyTenantColors } from '../utils/colorUtils';

type TenantInfoDto = components['schemas']['TenantInfoDto'];

interface TenantSelectionProps {
  onTenantSelected: (tenant: TenantInfoDto) => void;
}

const TenantSelection: React.FC<TenantSelectionProps> = ({
  onTenantSelected,
}) => {
  const navigate = useNavigate();
  const { user, getIdToken } = useCognitoAuth();
  const [tenants, setTenants] = useState<TenantInfoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<TenantInfoDto | null>(
    null,
  );

  const handleTenantSelect = (tenant: TenantInfoDto) => {
    setSelectedTenant(tenant);
    // Immediately proceed with the selection - no confirmation needed
    handleConfirmSelection(tenant);
  };

  const handleConfirmSelection = useCallback(
    (tenant?: TenantInfoDto) => {
      const tenantToUse = tenant ?? selectedTenant;
      if (tenantToUse) {
        // Apply tenant colors immediately
        if (tenantToUse.appColors) {
          applyTenantColors(tenantToUse.appColors);
        }

        // Navigate to dashboard with tenantId parameter
        if (tenantToUse.id) {
          navigate(`/dashboard?tenantId=${tenantToUse.id}`, { replace: true });
        }

        onTenantSelected(tenantToUse);
      }
    },
    [selectedTenant, onTenantSelected, navigate],
  );

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setLoading(true);
        setError(null);

        // Ensure we have a valid token before making the API call
        const token = getIdToken();
        if (!token) {
          setError(
            'Authentication token not available. Please try logging in again.',
          );
          return;
        }

        // Add a small delay to ensure JWT middleware is properly set up
        await new Promise((resolve) => setTimeout(resolve, 100));

        const response = await OpenAPI.GET(
          '/api/v2/admin/configuration/user-tenants',
        );

        if (response.data) {
          setTenants(response.data);
          // If only one tenant, auto-select it
          if (response.data.length === 1) {
            const tenant = response.data[0];
            setSelectedTenant(tenant);
            // Auto-continue with single tenant after a short delay
            setTimeout(() => {
              handleConfirmSelection(tenant);
            }, 1000);
          }
        } else {
          setError('No tenants found for your account');
        }
      } catch (err) {
        setError('Failed to load tenants. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    void fetchTenants();
  }, [getIdToken, handleConfirmSelection]);

  const getTenantInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ minHeight: '100vh' }}
      >
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center">
          <ProgressSpinner
            style={{ width: '4rem', height: '4rem' }}
            strokeWidth="4"
            className="mb-4"
          />
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            Loading Organizations
          </h2>
          <p className="text-gray-600 text-lg">
            Please wait while we fetch your available organizations...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ minHeight: '100vh' }}
      >
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="pi pi-exclamation-triangle text-red-600 text-2xl" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            Unable to Load Organizations
          </h2>
          <Message
            severity="error"
            text={error}
            className="mb-6 w-full text-lg"
          />
          <Button
            label="Try Again"
            icon="pi pi-refresh"
            onClick={() => {
              window.location.reload();
            }}
            className="w-full p-3 text-lg"
          />
        </div>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ minHeight: '100vh' }}
      >
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="pi pi-building text-gray-400 text-2xl" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            No Organizations Available
          </h2>
          <p className="text-gray-600 text-lg mb-6">
            You do not have access to any organizations yet. Please contact your
            administrator.
          </p>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={() => {
              window.location.reload();
            }}
            className="w-full p-3 text-lg"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen surface-ground">
      {/* Content */}
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <Card className="mb-4">
            <div className="bg-primary p-4 border-round-top-2xl">
              <div className="flex align-items-center gap-4">
                <Avatar
                  label={user?.email?.charAt(0).toUpperCase() ?? 'U'}
                  className="w-3rem h-3rem border-round-lg bg-white-alpha-20 text-white font-bold text-xl"
                />
                <div>
                  <h1 className="text-2xl font-bold text-white m-0">
                    Welcome, {user?.email?.split('@')[0] ?? 'User'}!
                  </h1>
                  <p className="text-blue-100 m-0">
                    Please select an organization to continue
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Tenant Grid */}
          <Card>
            <h2 className="text-lg font-semibold text-900 mb-4">
              Available Organizations ({tenants.length})
            </h2>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tenants.map((tenant) => {
                const tenantColor =
                  tenant.appColors?.find((c) => c.type === 'P400')?.value ??
                  '#3B82F6';
                const isSelected = selectedTenant?.id === tenant.id;

                return (
                  <Card
                    key={tenant.id}
                    className={classNames(
                      'cursor-pointer transition-all transition-duration-200 hover:shadow-4',
                      isSelected
                        ? 'border-2 border-primary shadow-3'
                        : 'border-1 border-gray-200 hover:border-gray-300',
                    )}
                    onClick={() => {
                      handleTenantSelect(tenant);
                    }}
                  >
                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-0 right-0 mt-3 mr-3">
                        <div className="w-1-5rem h-1-5rem bg-primary border-circle flex align-items-center justify-content-center">
                          <i className="pi pi-check text-white text-sm" />
                        </div>
                      </div>
                    )}

                    {/* Tenant Avatar */}
                    <div className="mb-4">
                      <Avatar
                        label={getTenantInitials(tenant.name)}
                        className="w-4rem h-4rem border-round-xl text-white font-bold text-xl shadow-3 hover:shadow-4 transition-all transition-duration-200"
                        style={{
                          background: `linear-gradient(135deg, ${tenantColor}, ${tenantColor}dd)`,
                        }}
                      />
                    </div>

                    {/* Tenant Info */}
                    <div>
                      <h3
                        className={classNames(
                          'font-semibold text-lg mb-2 transition-colors transition-duration-200',
                          isSelected ? 'text-primary' : 'text-900',
                        )}
                      >
                        {tenant.name}
                      </h3>
                      {tenant.description && (
                        <p className="text-600 text-sm line-height-3 mb-3">
                          {tenant.description}
                        </p>
                      )}
                      {tenant.parentName && (
                        <div className="flex align-items-center text-xs text-500 mb-2">
                          <i className="pi pi-sitemap mr-1" />
                          Parent: {tenant.parentName}
                        </div>
                      )}
                      <div className="text-xs text-500">ID: {tenant.id}</div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantSelection;
